/**
 * Markdown → biztonságos HTML.
 *
 * A `.md` fájl idegen forrásból is jöhet (AI, ügyfél, letöltés), ezért a
 * kimenetet mindig DOMPurify-jal tisztítjuk. A kódblokk highlight és a mermaid
 * dinamikus importtal jön, hogy az alap bundle kicsi maradjon.
 */
import MarkdownIt from 'markdown-it';
import anchor from 'markdown-it-anchor';
// @ts-expect-error - a csomaghoz nincs típusdefiníció
import taskLists from 'markdown-it-task-lists';
import DOMPurify from 'dompurify';

export interface RenderResult {
  html: string;
  frontMatter: string | null;
  headings: { level: number; text: string; slug: string }[];
  hasMermaid: boolean;
}

let hljs: typeof import('highlight.js').default | null = null;

/** Előtölti a syntax highlightert (lusta betöltés). */
export async function loadHighlighter(): Promise<void> {
  if (!hljs) hljs = (await import('highlight.js/lib/common')).default;
}

const md: MarkdownIt = new MarkdownIt({
  html: true, // a sanitizálást a DOMPurify végzi
  linkify: true,
  breaks: false,
  typographer: false,
  highlight(code, lang) {
    if (lang === 'mermaid') {
      // A forrást a code elem szövegében hagyjuk (attribútumban a sanitizer levágná),
      // a diagrammá alakítás a render/mermaid.ts dolga.
      return `<pre class="mermaid-block"><code>${escapeHtml(code)}</code></pre>`;
    }
    if (hljs && lang && hljs.getLanguage(lang)) {
      try {
        const out = hljs.highlight(code, { language: lang, ignoreIllegals: true }).value;
        return `<pre class="code-block"><code class="hljs language-${escapeAttr(lang)}">${out}</code></pre>`;
      } catch {
        /* esik vissza a nyers változatra */
      }
    }
    return `<pre class="code-block"><code class="hljs">${escapeHtml(code)}</code></pre>`;
  },
});

md.use(anchor, { slugify, permalink: false });
md.use(taskLists, { enabled: true, label: true });

// A linkek új fülön nyíljanak, és ne vigyenek magukkal referrert.
const defaultLinkOpen =
  md.renderer.rules.link_open ??
  ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));
md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const href = tokens[idx].attrGet('href') ?? '';
  if (/^https?:/i.test(href)) {
    tokens[idx].attrSet('target', '_blank');
    tokens[idx].attrSet('rel', 'noopener noreferrer');
  }
  return defaultLinkOpen(tokens, idx, options, env, self);
};

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

/** Levágja a YAML front mattert a törzsről. */
export function splitFrontMatter(source: string): { frontMatter: string | null; body: string } {
  const match = /^﻿?---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(source);
  if (!match) return { frontMatter: null, body: source };
  return { frontMatter: match[1], body: source.slice(match[0].length) };
}

/** Fejléclista a tartalomjegyzékhez. */
function collectHeadings(source: string) {
  const headings: RenderResult['headings'] = [];
  const lines = source.split(/\r?\n/);
  let inFence = false;
  for (const line of lines) {
    if (/^\s*(```|~~~)/.test(line)) inFence = !inFence;
    if (inFence) continue;
    const m = /^(#{1,4})\s+(.*)$/.exec(line);
    if (m) {
      const text = m[2].replace(/#+\s*$/, '').trim();
      headings.push({ level: m[1].length, text, slug: slugify(text) });
    }
  }
  return headings;
}

export function render(source: string): RenderResult {
  const { frontMatter, body } = splitFrontMatter(source);
  const raw = md.render(body);
  const html = DOMPurify.sanitize(raw, {
    ADD_ATTR: ['target', 'rel', 'id', 'class', 'data-src', 'checked', 'disabled'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
    FORBID_ATTR: ['srcdoc', 'onerror', 'onload'],
  });
  return {
    html,
    frontMatter,
    headings: collectHeadings(body),
    hasMermaid: /```mermaid/.test(body),
  };
}

/** Durva olvasásiidő-becslés a státuszsorhoz. */
export function stats(source: string): { words: number; minutes: number } {
  const words = source.trim() ? source.trim().split(/\s+/).length : 0;
  return { words, minutes: Math.max(1, Math.round(words / 200)) };
}
